/**
 * MFCategoryRankings — Phase B
 *
 * Shows the investable-universe category dashboard:
 *  • Category pill selector (24 categories)
 *  • Stats card: count, median score, median 3Y CAGR, median Sharpe, median Max DD
 *  • Best / Median / Worst fund summaries
 *  • Full ranked table: rank · fund · score · confidence · 3Y CAGR · Sharpe · DD · nav history
 */

import React, { useState, useEffect, useMemo } from 'react'
import {
  TrendingUp, TrendingDown, Shield, Activity, RefreshCw,
  ArrowUp, ArrowDown, ChevronDown, ChevronRight,
} from 'lucide-react'
import { mfIntelAPI } from '../services/api'
import MFExplainPanel from './MFExplainPanel'
import LoadingSpinner from './LoadingSpinner'

// ── Helpers ────────────────────────────────────────────────────────────────────

const fmt = (v, d = 2, suffix = '') =>
  v != null ? `${Number(v).toFixed(d)}${suffix}` : '—'

const pctBadge = (v) => {
  if (v == null) return <span className="text-gray-400">—</span>
  const n = Number(v)
  return (
    <span className={n >= 0 ? 'text-success-600 font-medium' : 'text-danger-600 font-medium'}>
      {n >= 0 ? '+' : ''}{n.toFixed(2)}%
    </span>
  )
}

const ConfBadge = ({ level }) => {
  const styles = {
    HIGH:   'bg-success-100 text-success-700 border border-success-200',
    MEDIUM: 'bg-amber-100   text-amber-700   border border-amber-200',
    LOW:    'bg-danger-100  text-danger-700  border border-danger-200',
  }
  return (
    <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded uppercase tracking-wide ${styles[level] || styles.LOW}`}>
      {level}
    </span>
  )
}

const DQBadge = ({ dq }) => {
  const styles = { GOOD: 'text-success-600', PARTIAL: 'text-amber-600', POOR: 'text-danger-500' }
  return <span className={`text-[10px] ${styles[dq] || 'text-gray-400'}`}>{dq || '—'}</span>
}

const ScoreBar = ({ score }) => {
  const s = score ?? 0
  const color = s >= 70 ? 'bg-success-500' : s >= 50 ? 'bg-amber-400' : 'bg-danger-400'
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${s}%` }} />
      </div>
      <span className="text-xs font-semibold tabular-nums">{fmt(s, 1)}</span>
    </div>
  )
}

// Category groups for the pill nav
const CATEGORY_GROUPS = [
  {
    label: 'Equity',
    categories: [
      'Large Cap', 'Mid Cap', 'Small Cap', 'Flexi Cap', 'Multi Cap',
      'Large & Mid Cap', 'Focused', 'Value', 'Contra', 'Dividend Yield', 'ELSS',
    ],
  },
  {
    label: 'Hybrid',
    categories: ['Aggressive Hybrid', 'Conservative Hybrid', 'Equity Savings', 'Balanced Advantage', 'Multi Asset'],
  },
  {
    label: 'Debt',
    categories: ['Corporate Bond', 'Banking & PSU', 'Money Market', 'Liquid', 'Short Duration', 'Medium Duration', 'Gilt'],
  },
  {
    label: 'Gold',
    categories: ['Gold'],
  },
]

// ── Sort header — defined OUTSIDE the component for stable React identity
const SortTh = ({ field, label, right = false, sortKey, sortDir, onSort }) => {
  const active = sortKey === field
  return (
    <th
      onClick={() => onSort(field)}
      className={`px-3 py-2.5 text-xs font-medium text-gray-500 cursor-pointer select-none hover:bg-gray-100 whitespace-nowrap ${right ? 'text-right' : 'text-left'}`}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active
          ? (sortDir === 'asc' ? <ArrowUp className="w-3 h-3 text-primary-500" /> : <ArrowDown className="w-3 h-3 text-primary-500" />)
          : <ArrowUp className="w-3 h-3 text-gray-300" />}
      </span>
    </th>
  )
}

// ── Main component ─────────────────────────────────────────────────────────────

const MFCategoryRankings = () => {
  const [dashboard, setDashboard] = useState([])   // full data from API
  const [loading,   setLoading]   = useState(true)
  const [error,     setError]     = useState(null)
  const [selected,  setSelected]  = useState('Small Cap')
  const [sortKey,   setSortKey]   = useState('rank')
  const [sortDir,   setSortDir]   = useState('asc')
  const [search,    setSearch]    = useState('')
  const [expandedFund, setExpandedFund]     = useState(null)
  const [explainData,  setExplainData]      = useState({})   // { [schemeCode]: data|'loading'|'error' }

  const loadExplain = async (code) => {
    if (explainData[code]) return   // already loaded
    setExplainData(prev => ({ ...prev, [code]: 'loading' }))
    try {
      const res = await mfIntelAPI.explainScheme(code)
      setExplainData(prev => ({ ...prev, [code]: res.data }))
    } catch (err) {
      setExplainData(prev => ({ ...prev, [code]: 'error' }))
    }
  }

  const handleExpandFund = (code) => {
    const next = expandedFund === code ? null : code
    setExpandedFund(next)
    if (next) loadExplain(next)
  }

  // Fetch all categories once on mount
  useEffect(() => {
    const load = async () => {
      setLoading(true)
      setError(null)
      try {
        const res = await mfIntelAPI.auditCategoryDashboard()
        setDashboard(res.data.categories || [])
      } catch (err) {
        setError(err.response?.data?.message || err.message || 'Failed to load rankings')
      } finally {
        setLoading(false)
      }
    }
    load()
  }, [])

  const catData = useMemo(
    () => dashboard.find(c => c.label === selected) || null,
    [dashboard, selected]
  )

  const displayFunds = useMemo(() => {
    if (!catData?.funds) return []
    const q = search.toLowerCase()
    const base = q
      ? catData.funds.filter(f => f.schemeName.toLowerCase().includes(q) || f.amc.toLowerCase().includes(q))
      : catData.funds

    return [...base].sort((a, b) => {
      const keyMap = { rank: 'rank', score: 'score', cagr3Y: 'cagr3Y', sharpe: 'sharpe', maxDD: 'maxDrawdown', navCount: 'navCount' }
      const k = keyMap[sortKey] || sortKey
      const va = a[k] ?? (sortDir === 'asc' ? Infinity : -Infinity)
      const vb = b[k] ?? (sortDir === 'asc' ? Infinity : -Infinity)
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1)
    })
  }, [catData, sortKey, sortDir, search])

  const handleSort = (field) => {
    setSortKey(field)
    setSortDir(d => field === sortKey ? (d === 'asc' ? 'desc' : 'asc') : 'asc')
  }

  if (loading) return <div className="flex items-center justify-center py-16"><LoadingSpinner size="large" /></div>
  if (error)   return <div className="text-center py-16 text-danger-600 text-sm">{error}</div>

  return (
    <div className="space-y-4">

      {/* ── Category Pill Nav ─────────────────────────────────────────────── */}
      <div className="space-y-2">
        {CATEGORY_GROUPS.map(group => (
          <div key={group.label}>
            <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 mb-1.5">{group.label}</p>
            <div className="flex flex-wrap gap-1.5">
              {group.categories.map(cat => {
                const d = dashboard.find(c => c.label === cat)
                const count = d?.scoredCount ?? 0
                const active = selected === cat
                return (
                  <button
                    key={cat}
                    onClick={() => { setSelected(cat); setSearch(''); setExpandedFund(null) }}
                    className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                      active
                        ? 'bg-primary-600 text-white border-primary-600 shadow-sm'
                        : 'bg-white text-gray-600 border-gray-200 hover:border-primary-300 hover:text-primary-700'
                    }`}
                  >
                    {cat}
                    {count > 0 && <span className={`ml-1.5 ${active ? 'text-primary-200' : 'text-gray-400'}`}>({count})</span>}
                  </button>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {!catData && (
        <div className="text-center py-12 text-gray-400 text-sm">Select a category above</div>
      )}

      {catData && (
        <>
          {/* ── Stats Card ────────────────────────────────────────────────── */}
          <div className="bg-gradient-to-br from-primary-50 to-blue-50 border border-primary-100 rounded-xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-base font-bold text-gray-900">{catData.label}</h3>
                <p className="text-xs text-gray-500">
                  {catData.scoredCount} scored · {catData.investableCount} investable
                  {catData.investableCount > catData.scoredCount && (
                    <span className="text-amber-600 ml-1">({catData.investableCount - catData.scoredCount} need scoring)</span>
                  )}
                </p>
              </div>
              {catData.confidenceDistribution && (
                <div className="flex gap-2 text-xs">
                  <span className="text-success-600 font-semibold">H:{catData.confidenceDistribution.HIGH}</span>
                  <span className="text-amber-600 font-semibold">M:{catData.confidenceDistribution.MEDIUM}</span>
                  <span className="text-danger-600 font-semibold">L:{catData.confidenceDistribution.LOW}</span>
                </div>
              )}
            </div>

            {/* Median row */}
            <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
              {[
                { label: 'Median Score', value: fmt(catData.medians?.score, 1), icon: <Activity className="w-3 h-3" /> },
                { label: '3Y CAGR',      value: catData.medians?.cagr3Y != null ? `${catData.medians.cagr3Y > 0 ? '+' : ''}${catData.medians.cagr3Y}%` : '—' },
                { label: '1Y Return',    value: catData.medians?.return1Y != null ? `${catData.medians.return1Y > 0 ? '+' : ''}${catData.medians.return1Y}%` : '—' },
                { label: 'Sharpe',       value: fmt(catData.medians?.sharpe, 2) },
                { label: 'Max DD',       value: catData.medians?.maxDD != null ? `${catData.medians.maxDD}%` : '—' },
                { label: 'Volatility',   value: catData.medians?.vol != null ? `${catData.medians.vol}%` : '—' },
              ].map(({ label, value }) => (
                <div key={label} className="bg-white/70 rounded-lg p-2.5 text-center">
                  <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-0.5">{label}</p>
                  <p className="text-sm font-bold text-gray-800">{value}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── Best / Median / Worst ─────────────────────────────────────── */}
          {(catData.best || catData.median || catData.worst) && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {[
                { tier: catData.best,   label: '🥇 Best',   bg: 'bg-success-50 border-success-200' },
                { tier: catData.median, label: '⚖️ Median', bg: 'bg-gray-50 border-gray-200' },
                { tier: catData.worst,  label: '📉 Worst',  bg: 'bg-danger-50 border-danger-100' },
              ].filter(r => r.tier).map(({ tier, label, bg }) => (
                <div key={label} className={`border rounded-xl p-3 ${bg}`}>
                  <p className="text-xs font-semibold text-gray-500 mb-1">{label}</p>
                  <p className="text-sm font-bold text-gray-900 leading-tight">{tier.schemeName}</p>
                  <p className="text-xs text-gray-400 mb-2">{tier.amc}</p>
                  <div className="flex flex-wrap gap-2 text-xs">
                    <span className="font-semibold">Score {fmt(tier.score, 1)}</span>
                    {tier.cagr3Y != null && <span>3Y {tier.cagr3Y > 0 ? '+' : ''}{tier.cagr3Y}%</span>}
                    {tier.sharpe != null && <span>Sh {tier.sharpe}</span>}
                    {tier.maxDrawdown != null && <span>DD {tier.maxDrawdown}%</span>}
                  </div>
                  <div className="mt-1.5">
                    <ConfBadge level={tier.confidence} />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── Ranked Table ──────────────────────────────────────────────── */}
          {catData.scoredCount === 0 ? (
            <div className="text-center py-10 text-gray-400 text-sm border border-dashed rounded-xl">
              No scored funds yet in {catData.label}.
              {catData.investableCount > 0 && ` ${catData.investableCount} investable funds need scoring — run compute-category first.`}
            </div>
          ) : (
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              {/* Search bar */}
              <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200">
                <input
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  placeholder={`Search ${catData.label} funds…`}
                  className="input py-1.5 text-sm flex-1 max-w-xs"
                />
                <span className="text-xs text-gray-400">{displayFunds.length} of {catData.scoredCount} funds</span>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-200">
                      <SortTh field="rank"     label="#"       sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-xs font-medium text-gray-500 text-left">Fund</th>
                      <SortTh field="score"    label="Score"   right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-xs font-medium text-gray-500 text-center">Confidence</th>
                      <SortTh field="cagr3Y"   label="3Y CAGR" right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortTh field="sharpe"   label="Sharpe"  right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortTh field="maxDD"    label="Max DD"  right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <SortTh field="navCount" label="History" right sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
                      <th className="px-3 py-2.5 text-xs font-medium text-gray-500 text-center">Quality</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {displayFunds.map(f => (
                      <React.Fragment key={f.schemeCode}>
                        <tr
                          className="hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => handleExpandFund(f.schemeCode)}
                        >
                          {/* Rank */}
                          <td className="px-3 py-2.5 tabular-nums">
                            <div className="flex items-center gap-1">
                              <span className="text-xs font-bold text-gray-500">#{f.rank}</span>
                              <span className="text-[10px] text-gray-300">{f.percentile?.toFixed(0)}p</span>
                            </div>
                          </td>

                          {/* Fund name */}
                          <td className="px-3 py-2.5 min-w-[220px] max-w-[320px]">
                            <div className="flex items-center gap-1">
                              {expandedFund === f.schemeCode
                                ? <ChevronDown className="w-3 h-3 text-gray-400 shrink-0" />
                                : <ChevronRight className="w-3 h-3 text-gray-300 shrink-0" />}
                              <div>
                                <p className="font-medium text-gray-900 text-xs leading-tight">{f.schemeName}</p>
                                <p className="text-[10px] text-gray-400">{f.amc}</p>
                              </div>
                            </div>
                          </td>

                          {/* Score bar */}
                          <td className="px-3 py-2.5 text-right"><ScoreBar score={f.score} /></td>

                          {/* Confidence */}
                          <td className="px-3 py-2.5 text-center"><ConfBadge level={f.confidence} /></td>

                          {/* 3Y CAGR */}
                          <td className="px-3 py-2.5 text-right">{pctBadge(f.cagr3Y)}</td>

                          {/* Sharpe */}
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            <span className={f.sharpe != null ? (f.sharpe > 0.5 ? 'text-success-600' : f.sharpe < 0 ? 'text-danger-600' : 'text-gray-700') : 'text-gray-400'}>
                              {f.sharpe != null ? Number(f.sharpe).toFixed(2) : '—'}
                            </span>
                          </td>

                          {/* Max Drawdown */}
                          <td className="px-3 py-2.5 text-right tabular-nums">
                            <span className={f.maxDrawdown != null ? (f.maxDrawdown < 20 ? 'text-success-600' : f.maxDrawdown > 40 ? 'text-danger-600' : 'text-amber-600') : 'text-gray-400'}>
                              {f.maxDrawdown != null ? `${f.maxDrawdown}%` : '—'}
                            </span>
                          </td>

                          {/* Nav history */}
                          <td className="px-3 py-2.5 text-right tabular-nums text-xs text-gray-500">
                            {f.navCount != null ? `${(f.navCount / 252).toFixed(1)}yr` : '—'}
                          </td>

                          {/* Data quality */}
                          <td className="px-3 py-2.5 text-center"><DQBadge dq={f.dataQuality} /></td>
                        </tr>

                        {/* Expanded explain panel — loads lazily on row click */}
                        {expandedFund === f.schemeCode && (
                          <tr className="bg-gray-50/80">
                            <td colSpan={9} className="px-4 py-4">
                              {explainData[f.schemeCode] === 'loading' && (
                                <div className="flex items-center gap-2 text-xs text-gray-400">
                                  <RefreshCw className="w-3 h-3 animate-spin" /> Loading explain data…
                                </div>
                              )}
                              {explainData[f.schemeCode] === 'error' && (
                                <p className="text-xs text-danger-500">Failed to load explain data for this fund.</p>
                              )}
                              {explainData[f.schemeCode] && explainData[f.schemeCode] !== 'loading' && explainData[f.schemeCode] !== 'error' && (
                                <MFExplainPanel data={explainData[f.schemeCode]} compact />
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default MFCategoryRankings
